'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'

/**
 * Default avatar SVG component used as a fallback when no avatar image is available
 */
export function DefaultAvatar({ 
  className = '',
  size = 24 
}: { 
  className?: string
  size?: number 
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  )
}

/**
 * Avatar component that displays a user's photo or a default SVG avatar
 * Uses regular img tag for external URLs (Firebase auth URLs) to avoid Next.js Image optimization issues
 * Uses Next.js Image for internal/local URLs
 */
export function Avatar({ 
  src, 
  alt, 
  size = 32,
  className = '',
  verified = false
}: { 
  src?: string | null
  alt: string
  size?: number
  className?: string
  verified?: boolean
}) {
  const [imageError, setImageError] = useState(false)

  // Reset error state when src changes
  useEffect(() => {
    setImageError(false)
  }, [src])

  // Normalize src: trim whitespace and ensure it's a valid non-empty string
  const normalizedSrc = src?.trim() ?? null

  // Calculate badge icon size based on avatar size - make it bigger
  const badgeIconSize = Math.max(size * 0.36, 20)

  // Wrap avatar and badge in a container so badge can be positioned outside overflow-hidden
  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }}>
      {/* Avatar circle */}
      <div
        className="rounded-full overflow-hidden w-full h-full"
        style={{ width: size, height: size }}
      >
        {normalizedSrc && !imageError ? (
          (() => {
            const isExternalUrl = normalizedSrc.startsWith('http://') || normalizedSrc.startsWith('https://')
            return isExternalUrl ? (
              // Use regular img tag for external URLs (Firebase auth URLs and Firebase Storage URLs)
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={normalizedSrc}
                alt={alt}
                width={size}
                height={size}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              // Use Next.js Image for internal URLs
              <Image
                src={normalizedSrc}
                alt={alt}
                width={size}
                height={size}
                className="w-full h-full object-cover"
              />
            )
          })()
        ) : (
          <div className="rounded-full bg-gray-200 flex items-center justify-center text-gray-500 w-full h-full">
            <DefaultAvatar size={Math.floor(size * 0.4)} />
          </div>
        )}
      </div>
      
      {/* Verified Badge - positioned at bottom-right, partially overlaying avatar (~50%) */}
      {verified && (
        <div 
          className="absolute z-10"
          style={{ 
            bottom: -4,
            right: -badgeIconSize / 4,
          }}
        >
          {/* White background circle */}
          <div 
            className="absolute bg-white rounded-full"
            style={{
              width: badgeIconSize * 0.6,
              height: badgeIconSize * 0.6,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          <CheckBadgeIcon 
            className="relative text-primary"
            style={{ 
              width: badgeIconSize, 
              height: badgeIconSize,
            }}
            title="Verified account"
          />
        </div>
      )}
    </div>
  )
}
